import type { FloorPlannerLayout } from '@repo/frontend-types';
import {
  asArray,
  classifyItemChange,
  classifySurfaceChange,
  computeVersionDelta,
  isLive,
  layoutCurrentDesigns,
  MOVE_MIN_M,
  ROTATE_MIN_DEG,
} from '@repo/front-end-utils';
import type { DraftEntityChanges, ItemDelta, SurfaceDelta } from '@repo/front-end-utils';

import type { Polygon } from '../../components/PolygonPlotter2/PolygonPlotter2';
import { encodeRef } from './compositeRefOptions';
import type { CoverageState } from './entityCoverage';
import { LIVE_OBSERVER_MARKER } from './matchObserversToPlan';
import { observerRefid } from './observerSessionState';
import type { EntityGeometry } from './observerSessionState';
import type { DraftComparisonState } from './useDraftChanges';
import type { ModelOutcome } from './useLiveTwinConfiguration';

/**
 * Which of four states every zone and machine on the plan is in — the Observers stage's grouping
 * (`camera-observer-setup-attention`, epic `camera-observer-setup` ledger rows 40–45).
 *
 *   pending           — the observers read is still in flight, so nothing can be said yet
 *   added-unobserved  — added in this draft (not in production's plan), and no complete observer
 *   unobserved        — no complete observer and not added in this draft, and nothing totals it
 *   totalled          — no complete observer, but a complex measure adds it up, so it IS measured.
 *                       Not an attention state: the operator chose this, and listing it as a gap
 *                       is the defect `coverage-checklist` exists to remove (epic ledger row 81)
 *   check             — has an observer whose REFERENCE geometry differs from the entity's current
 *                       geometry beyond the model-diff tolerances (0.1 m, 1°)
 *   configured        — has an observer, and none of them needs checking
 *
 * ## Where the reference geometry comes from, and never from a guess (spec FR3)
 *
 * An observer drawn or edited THIS SESSION carries a stamp (`observerStamps.ts`) — the entity as it
 * stood at that moment. An observer LOADED from the live configuration (`:live-` in its refid) is
 * compared against the twin's RUNNING MODEL, read once per session and reversed into a plan: that
 * model is the persisted record of the geometry the observer was activated against, and comparing
 * the edited plan to it survives closing and reopening the wizard. An observer with neither
 * reference — a plan-held observer on a twin whose model read returned `none` or `failed` — is
 * never flagged: absent is not errored, and the stage says separately when the model could not be
 * read.
 *
 * ## Two functions, because they change at different rates
 *
 * `computeModelChanges` walks the running model against the whole plan — the expensive half — and
 * depends only on the model, the layout and the twin. `classifyEntityAttention` is the per-entity
 * loop over session state, which changes on every polygon vertex the operator drags. The stage
 * memoises them separately so a drag never re-diffs the twin.
 *
 * Both are pure: no React, no store. They are tested against the real NP2-STN fixtures
 * (160 entities, 151 live observers) the stage's other pure functions already use.
 */
export type AttentionState =
  | 'pending'
  | 'added-unobserved'
  | 'unobserved'
  | 'totalled'
  | 'check'
  | 'configured';

export interface EntityAttention {
  state: AttentionState;
  /** The FR4 wording for `check` — `moved 1.4 m`, `rotated 90°`, `resized`, `reshaped`, joined by a comma. */
  reason?: string;
  /** Centre-to-centre displacement in metres where the reason includes a move. */
  distanceM?: number;
}

/** One entity's change against its reference, in the words the row shows. */
export interface GeometryChange {
  reason: string;
  distanceM?: number;
}

/** The running model's differences from the plan, by plan refid. Empty when the model is not `ready`. */
export type ModelChanges = ReadonlyMap<string, GeometryChange>;

/** The model-diff tolerances, in the shape the classifiers take. */
const TOLERANCES = {
  positionEpsilon: MOVE_MIN_M,
  rotationEpsilon: ROTATE_MIN_DEG,
  wrapRotation: true,
};

const round1 = (n: number): string => (Math.round(n * 10) / 10).toFixed(1);

/** `never`-checked, so a change type the comparison learns later is a compile error here, not a silent `configured`. */
const describeItem = (
  change: Pick<ItemDelta, 'changeType' | 'distance' | 'rotation'>,
): GeometryChange | null => {
  switch (change.changeType) {
    case 'moved':
      return { reason: `moved ${round1(change.distance ?? 0)} m`, distanceM: change.distance };
    case 'rotated':
      return { reason: `rotated ${Math.round(change.rotation ?? 0)}°` };
    case 'moved-and-rotated':
      return {
        reason: `moved ${round1(change.distance ?? 0)} m, rotated ${Math.round(change.rotation ?? 0)}°`,
        distanceM: change.distance,
      };
    case 'added':
    case 'removed':
      return null;
    default: {
      const exhaustive: never = change.changeType;
      return exhaustive;
    }
  }
};

const describeSurface = (
  change: Pick<SurfaceDelta, 'changeType' | 'distance'>,
): GeometryChange | null => {
  switch (change.changeType) {
    case 'moved':
      return { reason: `moved ${round1(change.distance ?? 0)} m`, distanceM: change.distance };
    case 'resized':
      return { reason: 'resized' };
    case 'reshaped':
      return { reason: 'reshaped' };
    case 'moved-and-reshaped':
      return {
        reason: `moved ${round1(change.distance ?? 0)} m, reshaped`,
        distanceM: change.distance,
      };
    default: {
      const exhaustive: never = change.changeType;
      return exhaustive;
    }
  }
};

/** A session stamp against the entity's current geometry, with the model-diff tolerances. */
const changeSinceStamp = (
  stamp: EntityGeometry,
  now: EntityGeometry | undefined,
): GeometryChange | null => {
  if (!now || now.kind !== stamp.kind) return null;
  if (stamp.kind === 'item' && now.kind === 'item') {
    const change = classifyItemChange(stamp, now, TOLERANCES);
    return change ? describeItem(change) : null;
  }
  if (stamp.kind === 'surface' && now.kind === 'surface') {
    const change = classifySurfaceChange(stamp.poly, now.poly, TOLERANCES);
    return change ? describeSurface(change) : null;
  }
  return null;
};

/**
 * The running model against the edited plan, keyed by the plan's refids — computed once per
 * (model, layout, twin). Empty when the model read is not `ready`: `none` and `failed` both mean
 * "no reference", never "no change".
 */
export const computeModelChanges = ({
  model,
  layout,
  twinBid,
}: {
  model: ModelOutcome | undefined;
  layout: FloorPlannerLayout | null | undefined;
  twinBid: string | undefined;
}): ModelChanges => {
  const changes = new Map<string, GeometryChange>();
  if (model?.status !== 'ready' || !layout) return changes;
  const delta = computeVersionDelta(model.value, layout, 'all', {
    matchBy: 'identity',
    twinBid,
    compareSurfaces: true,
    ...TOLERANCES,
  });
  for (const d of delta.items) {
    const described = describeItem(d);
    if (described) changes.set(d.refid, described);
  }
  for (const d of delta.surfaces) {
    const described = describeSurface(d);
    if (described) changes.set(d.refid, described);
  }
  return changes;
};

/**
 * The spaces and machines the draft added against production (`onboarding-status-colours-observers`,
 * R2), from the page's change source: `null` — "not known" — unless the comparison is `ready`, so a
 * comparison that is off, loading, failed or for a twin never launched marks nothing as added. An
 * empty set is "known, nothing added". Cameras, observers and measures are not entities this step
 * classifies, so only `zones` and `placedEquipment` count.
 */
export const addedRefidsFrom = (
  byEntity: DraftEntityChanges | undefined,
  comparison: DraftComparisonState | undefined,
): ReadonlySet<string> | null => {
  if (comparison !== 'ready' || !byEntity) return null;
  const added = new Set<string>();
  byEntity.forEach((change, refid) => {
    if (change.added && (change.kind === 'zones' || change.kind === 'placedEquipment')) {
      added.add(refid);
    }
  });
  return added;
};

export interface ClassifyAttentionInput {
  layout: FloorPlannerLayout | null | undefined;
  /**
   * The entities added in this draft ({@link addedRefidsFrom}). `null` marks nothing as added; a
   * refid that is not among the entities is ignored.
   */
  addedRefids: ReadonlySet<string> | null;
  polygonsByEntity: Record<string, Polygon[]>;
  /** Draw-time stamps by observer refid (`observerStamps.ts`). */
  stamps: Record<string, EntityGeometry>;
  /** From {@link computeModelChanges}. */
  modelChanges: ModelChanges;
  /**
   * What measures each thing, from `entityCoverage.ts` — the ONE rule this stage, the Launch
   * checklist and the complex-measures editor now share (epic ledger row 82). Keyed by the encoded
   * composite reference, so a lookup here costs one `encodeRef` per entity in a walk that already
   * knows the entity's kind.
   *
   * Absent (an empty map) is safe and means what it says: nothing totals anything, which is the
   * behaviour this stage had before coverage existed.
   */
  coverage: ReadonlyMap<string, CoverageState>;
  countsLoading: boolean;
}

export const classifyEntityAttention = ({
  layout,
  addedRefids,
  polygonsByEntity,
  stamps,
  modelChanges,
  coverage,
  countsLoading,
}: ClassifyAttentionInput): Record<string, EntityAttention> => {
  const result: Record<string, EntityAttention> = {};
  // One walk collects both the entity list and each entity's current geometry, so the stamp
  // comparison below is a lookup rather than a scan per stamped observer.
  const entities: { refid: string; kind: 'surface' | 'item' }[] = [];
  const geometryByRefid = new Map<string, EntityGeometry>();
  for (const design of layoutCurrentDesigns(layout ?? null)) {
    for (const s of asArray(design?.surfaces).filter(isLive)) {
      entities.push({ refid: s.refid, kind: 'surface' });
      geometryByRefid.set(s.refid, {
        kind: 'surface',
        poly: asArray(s.poly).map(p => ({ x: p.x, y: p.y })),
      });
    }
    for (const i of asArray(design?.items).filter(isLive)) {
      entities.push({ refid: i.refid, kind: 'item' });
      geometryByRefid.set(i.refid, { kind: 'item', x: i.x, y: i.y, rotation: i.rotation });
    }
  }

  if (countsLoading) {
    for (const e of entities) result[e.refid] = { state: 'pending' };
    return result;
  }

  for (const entity of entities) {
    const complete = (polygonsByEntity[entity.refid] ?? []).filter(p => p.isComplete);
    if (complete.length === 0) {
      const addedInDraft = addedRefids?.has(entity.refid) === true;
      /*
       * ⚠ `added-unobserved` wins over `totalled`, deliberately.
       *
       * Something the operator added in this draft and has not measured is exactly what
       * that group is for, and a measure that happens to total it does not make the drawing
       * finished. The reverse order would quietly swallow new work into a group nobody checks.
       */
      const totalled =
        !addedInDraft &&
        coverage.get(
          encodeRef({ kind: 'entity', refid: entity.refid, entityKind: entity.kind }),
        ) === 'totalled';
      result[entity.refid] = {
        state: addedInDraft ? 'added-unobserved' : totalled ? 'totalled' : 'unobserved',
      };
      continue;
    }

    let flagged: GeometryChange | null = null;
    for (const polygon of complete) {
      const key = observerRefid(entity.refid, polygon.id);
      const stamp = stamps[key];
      const change = stamp
        ? changeSinceStamp(stamp, geometryByRefid.get(entity.refid))
        : key.includes(LIVE_OBSERVER_MARKER)
          ? (modelChanges.get(entity.refid) ?? null)
          : null;
      if (change) {
        flagged = change;
        break;
      }
    }
    result[entity.refid] = flagged
      ? { state: 'check', reason: flagged.reason, distanceM: flagged.distanceM }
      : { state: 'configured' };
  }

  return result;
};

/**
 * The three attention states, for counting and for the pink treatment.
 *
 * ⚠ **`totalled` is deliberately NOT here.** An entity a complex measure adds up is measured, so
 * it carries no pink stripe, no error glyph, and it does not count towards the stage's
 * "needs attention" line. That is the whole point of telling it apart from `unobserved`.
 */
export const NEEDS_ATTENTION: ReadonlySet<AttentionState> = new Set<AttentionState>([
  'added-unobserved',
  'unobserved',
  'check',
]);
